import {
	type ComponentType,
	Fragment,
	type FunctionComponent,
	type ReactElement,
	type ReactNode,
	memo,
	useEffect,
} from 'react';

import { lazyRetry } from '@shared/chunks';
import { type DelegatorProps, delegator } from '@shared/router/delegator';
import type { RouteDefinition } from '@shared/router/parser';

interface RouteParams {
	[key: string]: boolean | string | number;
}

export interface RouteComponentProps {
	children?: ReactNode;

	// Assigned from RouteDefinition
	name?: string;
	path?: string;

	params?: RouteParams;
	search?: RouteParams;
}

export type RouteComponent = ComponentType<RouteComponentProps>;

type RouteModule = Promise<{ default: RouteComponent }>;

export function view(factory: () => RouteModule): RouteComponent {
	return lazyRetry(factory);
}

export type RouteElement = ReactElement<
	IndexRouteProps | RouteProps | RouteGroupProps
>;

export type RouteTransformValue = boolean | string | number;

export type RouteTransformer = (
	value: RouteTransformValue
) => RouteTransformValue;

export interface RouteMatches {
	[key: string]: RegExp;
}

export interface RouteTransforms {
	[key: string]: RouteTransformer;
}

export type Middleware = (parameters: RouteDefinition) => RouteDefinition;

export interface RouteProps {
	path: string;
	name?: string;
	group?: string[];
	condition?: boolean;

	matches?: RouteMatches;
	transforms?: RouteTransforms;

	before?: Middleware[];
	after?: Middleware[];
	enter?: () => void;
	leave?: () => void;

	component: RouteComponent;
	wrapWith?: ReactNode | RouteComponent;
	children?: RouteElement | RouteElement[];
}

export const Route: FunctionComponent<RouteProps> = (): null => null;

export interface IndexRouteProps {
	name?: string;
	group?: string[];

	before?: Middleware[];
	after?: Middleware[];
	enter?: () => void;
	leave?: () => void;

	isRouteIndex?: boolean;

	component: RouteComponent;
	wrapWith?: ReactNode | RouteComponent;
}

export const IndexRoute: FunctionComponent<IndexRouteProps> = ({
	isRouteIndex = true,
}: IndexRouteProps): null => {
	if (isRouteIndex !== true) {
		throw new TypeError('Cannot override <IndexRoute.isRouteIndex> property.');
	}

	return null;
};

IndexRoute.displayName = 'IndexRoute';

export interface RouteGroupProps {
	name: string;
	path?: string;
	group?: string[];
	condition?: boolean;

	before?: Middleware[];
	after?: Middleware[];
	wrapWith?: ReactNode | RouteComponent;

	isRouteGroup?: boolean;
	children?: RouteElement | RouteElement[];
}

export interface RouteElementProps {
	path?: string;
	name?: string;
	group?: string[];
	condition?: boolean;

	matches?: RouteMatches;
	transforms?: RouteTransforms;

	before?: Middleware[];
	after?: Middleware[];
	enter?: () => void;
	leave?: () => void;

	isRouteIndex?: boolean;
	isRouteGroup?: boolean;

	component?: RouteComponent;
	wrapWith?: ReactNode | RouteComponent;
	children?: RouteElement | RouteElement[];
}

export const RouteGroup: FunctionComponent<RouteGroupProps> = ({
	isRouteGroup = true,
}: RouteGroupProps): null => {
	if (isRouteGroup !== true) {
		throw new TypeError('Cannot override <RouteGroup.isRouteGroup> property.');
	}

	return null;
};

RouteGroup.displayName = 'RouteGroup';

interface RenderedRouteProps {
	children?: ReactNode;
	enter?: () => void;
	leave?: () => void;
}

function RenderedRoute({
	children,
	enter = () => {},
	leave = () => {},
}: RenderedRouteProps): ReactElement {
	useEffect(() => {
		enter();

		return leave;
	}, [enter, leave]);

	return <Fragment>{children}</Fragment>;
}

export function DefaultNotFoundComponent(): ReactElement {
	return (
		<Fragment>
			<h1>404 Not Found</h1>
		</Fragment>
	);
}

export function DefaultErrorComponent(): ReactElement {
	return (
		<Fragment>
			<h1>Error</h1>
		</Fragment>
	);
}

export function render(route: RouteDefinition): ReactElement {
	const { name, path, params, search, enter, leave } = route;

	const props: RouteComponentProps = {
		name,
		path,
		params,
		search,
	};

	let node = <route.component key={name} {...props} />;

	node = (
		<RenderedRoute enter={enter} leave={leave}>
			{node}
		</RenderedRoute>
	);

	if (route.wrapWith) {
		node = (
			<route.wrapWith
				key={`wrapWith:${(route.wrapWith as Function).name}`}
				{...props}>
				{node}
			</route.wrapWith>
		);
	}

	if (route.wrapGroupWith) {
		node = (
			<route.wrapGroupWith
				key={`wrapGroupWith:${(route.wrapGroupWith as Function).name}`}
				{...props}>
				{node}
			</route.wrapGroupWith>
		);
	}

	if (route.wrapAllWith) {
		node = (
			<route.wrapAllWith
				key={`wrapAllWith:${(route.wrapAllWith as Function).name}`}
				{...props}>
				{node}
			</route.wrapAllWith>
		);
	}

	return node;
}

interface RouterProps extends DelegatorProps {
	routes: RouteDefinition[];
	currentPath: string;
}

function RouterComponent({
	routes,
	currentPath,

	beforeEach,
	afterEach,
	onError,
	onNotFound,

	wrapAllWith,
	renderErrorWith,
	renderNotFoundWith,
}: RouterProps): ReactElement {
	return render(
		delegator(currentPath, routes, {
			beforeEach,
			afterEach,
			onError,
			onNotFound,

			wrapAllWith,
			renderErrorWith,
			renderNotFoundWith,
		})
	);
}

export const Router = memo(RouterComponent);
